import { I18n } from '@affine/i18n';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import {
  renderStreamLainDisclosure,
  renderStreamLainDisclosurePill,
  renderStreamLainDisplayItemLeadingLabel,
  renderStreamLainNestedDisplayItemRow,
  streamLainDisclosureStyles,
} from '@blocksuite/affine-shared/utils';
import { cssVarV2 } from '@streamlain/theme/v2';
import { html, nothing, type TemplateResult } from 'lit';
import { nanoid } from 'nanoid';

import {
  animateStreamLainDropdownPanelClose,
  decorateStreamLainDropdownPanel,
  ensureStreamLainDropdownMenuChromeStyle,
  STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
} from '../../../../components/dropdown-panel-chrome/chrome';
import { streamLainDropdownMenuMiddleware } from '../ai-chat-input/dropdown-menu-chrome';
import {
  type AiDocSourceNavigationDetail,
  type AiMailSourceNavigationDetail,
  toAiDocSourceNavigationDetail,
} from './doc-search-source-navigation';

export type {
  AiDocSourceNavigationDetail,
  AiMailSourceNavigationDetail,
  AiSourceNavigationDetail,
} from './doc-search-source-navigation';

function untitledDocLabel(): string {
  return I18n.t('com.streamlain.ai.doc-search.untitled');
}

function pageLabel(pageIndex: number | undefined): string {
  return I18n.t('com.streamlain.ai.doc-search.page', {
    page: (pageIndex ?? 0) + 1,
  });
}

function hitsLabel(count: number): string {
  return I18n.t(
    count === 1
      ? 'com.streamlain.ai.doc-search.hit'
      : 'com.streamlain.ai.doc-search.hits',
    { count }
  );
}

export interface AiDocSourceResult {
  citationId?: string;
  docId: string;
  blockId?: string;
  sourceKind?: 'doc' | 'block' | 'pdf-page' | 'image';
  sourceType?: string;
  elementId?: string;
  parentBlockId?: string;
  parentFlavour?: string;
  noteBlockId?: string;
  displayMode?: string;
  docTitle?: string;
  attachmentName?: string;
  blobKey?: string;
  databaseId?: string;
  databaseRowId?: string;
  databaseColumnId?: string;
  databaseTitle?: string;
  databaseRowLabel?: string;
  databaseColumnName?: string;
  visibility?: 'semantic' | 'technical';
  technicalSourceKind?: string;
  pageIndex?: number;
  query?: string;
  snippet?: string;
  rects?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
}

export type AiDocSourcePillItem = AiDocSourceResult & { title: string };
export type NormalizedAiDocSourcePillItem = AiDocSourcePillItem & {
  citationId: string;
};

export interface AiMailSourcePillItem {
  citationId?: string;
  sourceKind: 'mail';
  title: string;
  messageId?: string;
  from?: string;
  receivedAt?: string;
  snippet?: string;
  docId?: never;
  blockId?: never;
  docTitle?: never;
  attachmentName?: never;
  pageIndex?: never;
  query?: never;
  rects?: never;
}

export interface AiWebSourcePillItem {
  citationId?: string;
  sourceKind: 'web';
  title: string;
  url: string;
  snippet?: string;
  messageId?: never;
  from?: never;
  receivedAt?: never;
  docId?: never;
  blockId?: never;
  docTitle?: never;
  attachmentName?: never;
  pageIndex?: never;
  query?: never;
  rects?: never;
}

export type AiSourcePillItem =
  | AiDocSourcePillItem
  | AiMailSourcePillItem
  | AiWebSourcePillItem;
export type NormalizedAiSourcePillItem = AiSourcePillItem & {
  citationId: string;
};

const MAX_RENDERED_SOURCE_RESULTS = 12;

export const docSearchSourceStyles = `
  .doc-source-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0;
    margin-top: 8px;
    animation: doc-source-pills-fade-in 160ms ease-out;
  }

  .doc-source-history-shell {
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 12px 0 0;
  }

  .doc-source-history-shell > .streamlain-inline-disclosure-trigger {
    width: fit-content;
    max-width: 100%;
  }

  .doc-source-history-shell .streamlain-inline-disclosure-body-inner {
    max-height: 320px;
    overflow-y: auto;
  }

  .doc-source-list .streamlain-display-row-leading-label {
    color: ${cssVarV2.text.secondary};
    font-size: 12px;
    font-weight: 500;
  }

  .doc-source-list .streamlain-nested-display-icon {
    border: 0;
    background: transparent;
  }

  .doc-source-hit-count {
    padding: 2px 7px;
    border-radius: 999px;
    background: ${cssVarV2.layer.background.hoverOverlay};
    color: ${cssVarV2.text.secondary};
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    white-space: nowrap;
  }

  .doc-source-list > .streamlain-nested-display-row,
  .doc-source-tree-group > summary {
    grid-template-columns: 12px 20px minmax(0, 1fr) auto;
  }

  .doc-source-list > .streamlain-nested-display-row
    > .streamlain-nested-display-chevron-slot,
  .doc-source-tree-group > summary > .streamlain-nested-display-chevron-slot {
    width: 12px;
    height: 20px;
    color: ${cssVarV2.icon.primary};
  }

  .doc-source-tree-group > summary .streamlain-nested-display-chevron {
    width: 12px;
    height: 12px;
    color: ${cssVarV2.icon.primary};
  }

  @keyframes doc-source-pills-fade-in {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .streamlain-inline-disclosure .doc-source-pills {
    padding: 0 8px 8px;
  }

  .streamlain-inline-disclosure .doc-source-list {
    padding: 8px;
  }

  .doc-source-pill {
    max-width: 220px;
  }

  .doc-source-pill .streamlain-inline-disclosure-summary,
  .doc-source-pill .streamlain-disclosure-pill-content {
    min-width: 0;
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .doc-source-citation-portal {
    width: min(300px, calc(100vw - 32px));
    padding: 12px;
  }

  .doc-source-citation-title {
    display: block;
    color: ${cssVarV2.text.primary};
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
  }

  .doc-source-citation-snippet {
    margin-top: 6px;
    color: ${cssVarV2.text.secondary};
    font-size: 12px;
    line-height: 17px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .doc-source-citation-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .doc-source-citation-open {
    border: 1px solid ${cssVarV2.button.primary};
    border-radius: 6px;
    background: ${cssVarV2.button.primary};
    color: #fff;
    padding: 4px 10px;
    font-size: 12px;
    line-height: 16px;
    cursor: pointer;
  }

  .doc-source-citation-open:hover {
    filter: brightness(1.05);
  }

  .doc-source-pill-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .doc-source-pill-count {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 0 1 auto;
  }

  .doc-source-pill-index {
    color: ${cssVarV2.text.secondary};
  }

  .doc-source-group {
    display: inline-flex;
  }

  .doc-source-trigger {
    max-width: 100%;
  }

  .doc-source-portal {
    width: min(360px, calc(100vw - 32px));
    padding: 8px;
  }

  .doc-source-overflow-note {
    display: block;
    padding: 6px 8px 2px;
    color: ${cssVarV2.text.secondary};
    font-size: 12px;
    line-height: 16px;
  }

  .doc-search-activity {
    margin: 6px 0 4px;
    color: ${cssVarV2.text.secondary};
    font-size: 12px;
    line-height: 18px;
  }

  .doc-search-activity-details {
    margin: 4px 0;
    color: ${cssVarV2.text.secondary};
    font-size: 12px;
  }

  .doc-search-activity-results {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;
  }

  .doc-search-activity-result {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  .doc-search-activity-result small {
    color: ${cssVarV2.text.secondary};
  }
`;

interface DocSourceGroup {
  type: 'document' | 'single';
  key: string;
  title: string;
  docTitle?: string;
  results: NormalizedAiSourcePillItem[];
}

interface DocSourcePortalController {
  abortController: AbortController;
  closing: boolean;
  directionTrackingInstalled: boolean;
  panelSelector: string;
  portal: HTMLElement | null;
}

const STREAMLAIN_DROPDOWN_SIDE_ATTR = 'data-streamlain-dropdown-side';

const sourcePortalControllers = new WeakMap<
  HTMLElement,
  DocSourcePortalController
>();

function renderDocSourcePortalStyles() {
  ensureStreamLainDropdownMenuChromeStyle();
  return html`<style>
    ${streamLainDisclosureStyles}
    ${docSearchSourceStyles}
  </style>`;
}

function getDocSourcePortalPanel(
  controller: DocSourcePortalController
): HTMLElement | null {
  const portal = controller.portal;
  if (!portal) return null;
  if (portal.matches(controller.panelSelector)) return portal;
  return portal.querySelector<HTMLElement>(controller.panelSelector);
}

function decorateDocSourcePortal(
  controller: DocSourcePortalController,
  trigger: HTMLElement,
  attempt = 0
) {
  const panel = getDocSourcePortalPanel(controller);
  if (!panel) {
    if (attempt < 8) {
      requestAnimationFrame(() =>
        decorateDocSourcePortal(controller, trigger, attempt + 1)
      );
    }
    return;
  }
  decorateStreamLainDropdownPanel(panel, trigger);
  installDocSourceTriggerDirectionTracking(controller, trigger, panel);
}

function syncDocSourceTriggerDirection(
  trigger: HTMLElement,
  panel: HTMLElement
) {
  const anchor = trigger.closest<HTMLElement>(
    '.streamlain-disclosure-overlay-anchor'
  );
  if (!anchor) return;

  const side = panel.getAttribute(STREAMLAIN_DROPDOWN_SIDE_ATTR);
  const direction =
    side === 'top' || side === 'bottom'
      ? side === 'bottom'
        ? 'down'
        : 'up'
      : panel.getBoundingClientRect().top +
            panel.getBoundingClientRect().height / 2 <
          trigger.getBoundingClientRect().top +
            trigger.getBoundingClientRect().height / 2
        ? 'up'
        : 'down';

  anchor.classList.toggle(
    'streamlain-disclosure-overlay-up',
    direction === 'up'
  );
  anchor.classList.toggle(
    'streamlain-disclosure-overlay-down',
    direction === 'down'
  );
}

function installDocSourceTriggerDirectionTracking(
  controller: DocSourcePortalController,
  trigger: HTMLElement,
  panel: HTMLElement
) {
  if (controller.directionTrackingInstalled) {
    syncDocSourceTriggerDirection(trigger, panel);
    return;
  }

  controller.directionTrackingInstalled = true;
  const syncDirection = () => syncDocSourceTriggerDirection(trigger, panel);
  syncDirection();
  requestAnimationFrame(syncDirection);

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(syncDirection);
    observer.observe(panel, {
      attributeFilter: [STREAMLAIN_DROPDOWN_SIDE_ATTR],
      attributes: true,
    });
    controller.abortController.signal.addEventListener(
      'abort',
      () => observer.disconnect(),
      { once: true }
    );
  }

  window.addEventListener('resize', syncDirection, {
    signal: controller.abortController.signal,
  });
  window.addEventListener('scroll', syncDirection, {
    capture: true,
    signal: controller.abortController.signal,
  });
}

function installDocSourcePortalDismissal(
  trigger: HTMLElement,
  controller: DocSourcePortalController
) {
  const signal = controller.abortController.signal;
  setTimeout(() => {
    document.addEventListener(
      'click',
      event => {
        const target = event.target as Node | null;
        if (!target) return;
        if (trigger.contains(target)) return;
        if (controller.portal?.contains(target)) return;
        closeDocSourcePortal(trigger);
      },
      { signal }
    );
  });
  document.addEventListener(
    'keydown',
    event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDocSourcePortal(trigger);
    },
    { signal }
  );
}

function isMailSourceResult(
  result: AiSourcePillItem
): result is AiMailSourcePillItem {
  return result.sourceKind === 'mail';
}

function isWebSourceResult(
  result: AiSourcePillItem
): result is AiWebSourcePillItem {
  return result.sourceKind === 'web';
}

function isDocSourceResult(
  result: AiSourcePillItem
): result is AiDocSourcePillItem {
  return !isMailSourceResult(result) && !isWebSourceResult(result);
}

function sourceListSummaryQuery(
  results: Array<NormalizedAiSourcePillItem>,
  fallbackQuery: string
): string {
  const query = fallbackQuery.trim();
  if (query) return query;

  for (const result of results) {
    if (!isDocSourceResult(result)) continue;
    const resultQuery = result.query?.trim();
    if (resultQuery) return resultQuery;
  }

  return '';
}

function sourceListSummary(
  groups: DocSourceGroup[],
  results: Array<NormalizedAiSourcePillItem>,
  fallbackQuery: string
): string {
  const query = sourceListSummaryQuery(results, fallbackQuery);
  if (!query) {
    return I18n.t(
      groups.length === 1
        ? 'com.streamlain.ai.doc-search.found-source-generic'
        : 'com.streamlain.ai.doc-search.found-sources-generic',
      { count: groups.length }
    );
  }

  return I18n.t(
    groups.length === 1
      ? 'com.streamlain.ai.doc-search.found-source'
      : 'com.streamlain.ai.doc-search.found-sources',
    { count: groups.length, query }
  );
}

function formatMailSourceDate(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function getMailSourceMeta(result: AiMailSourcePillItem): string {
  return [result.from, formatMailSourceDate(result.receivedAt)]
    .filter(Boolean)
    .join(' · ');
}

function getSourceGroupKey(result: NormalizedAiSourcePillItem): string {
  if (isMailSourceResult(result)) {
    return `mail:${result.messageId ?? ''}:${result.title}:${result.from ?? ''}:${result.receivedAt ?? ''}`;
  }
  if (isWebSourceResult(result)) {
    return `web:${result.url}`;
  }
  return `${result.docId}:${result.blockId ?? result.title}`;
}

function getSourceGroupTitle(result: NormalizedAiSourcePillItem): string {
  if (isMailSourceResult(result)) {
    return readableSourceTitle(result.title) || 'Mail';
  }
  if (isWebSourceResult(result)) {
    return readableSourceTitle(result.title) || result.url;
  }
  return (
    readableSourceTitle(result.title) ||
    readableSourceTitle(result.docTitle) ||
    untitledDocLabel()
  );
}

function getSourceTooltip(
  result: NormalizedAiSourcePillItem,
  label: string
): string {
  if (!isMailSourceResult(result)) {
    if (isWebSourceResult(result)) {
      return [label, result.url, result.snippet].filter(Boolean).join('\n');
    }
    return label;
  }
  return [label, getMailSourceMeta(result), result.snippet]
    .filter(Boolean)
    .join('\n');
}

function canNavigateSource(result: NormalizedAiSourcePillItem): boolean {
  if (isMailSourceResult(result)) return Boolean(result.messageId);
  if (isWebSourceResult(result)) return Boolean(result.url);
  return true;
}

function getSourceHitTitle(
  result: NormalizedAiSourcePillItem,
  group: DocSourceGroup
): string {
  const isPdf =
    isDocSourceResult(result) &&
    result.blockId &&
    Number.isInteger(result.pageIndex);
  if (!isPdf) return getSourceLabel(result);

  const attachmentTitle =
    readableSourceTitle(result.attachmentName) ||
    readableSourceTitle(stripPageSuffix(result.title)) ||
    group.title;
  return `${pageLabel(result.pageIndex)} · ${attachmentTitle}`;
}

function getSourceHitSnippet(result: NormalizedAiSourcePillItem): string {
  return (
    readableSourceSnippet(result.snippet) ||
    readableSourceSnippet(result.title) ||
    hitsLabel(1)
  );
}

function closeDocSourcePortal(trigger: HTMLElement) {
  const controller = sourcePortalControllers.get(trigger);
  if (!controller) {
    trigger.setAttribute('aria-expanded', 'false');
    return;
  }

  if (controller.closing || controller.abortController.signal.aborted) return;
  const panel = getDocSourcePortalPanel(controller);
  if (panel?.isConnected && typeof window.matchMedia === 'function') {
    controller.closing = true;
    animateStreamLainDropdownPanelClose(panel, () => {
      controller.abortController.abort();
    });
    return;
  }

  controller.abortController.abort();
}

function openDocSourcePortal(
  trigger: HTMLElement,
  host: EventTarget,
  group: DocSourceGroup,
  fallbackQuery: string
) {
  const abortController = new AbortController();
  const controller: DocSourcePortalController = {
    abortController,
    closing: false,
    directionTrackingInstalled: false,
    panelSelector: '.doc-source-portal',
    portal: null,
  };
  abortController.signal.addEventListener('abort', () => {
    sourcePortalControllers.delete(trigger);
    trigger.setAttribute('aria-expanded', 'false');
  });
  sourcePortalControllers.set(trigger, controller);
  trigger.setAttribute('aria-expanded', 'true');

  const { portal } = createLitPortal({
    template: html`${renderDocSourcePortalStyles()}
      <div class="doc-source-portal streamlain-disclosure-popover-shell">
        <div
          class="doc-source-portal-scroll streamlain-disclosure-popover-scroll"
        >
          <div class="streamlain-nested-display-list">
            ${group.results
              .slice(0, 20)
              .map((result, resultIndex, visibleHits) => {
                const citation = citationLabelForResult(result);
                const title = getSourceHitTitle(result, group);
                const snippet = getSourceHitSnippet(result);
                return renderSourceHitRow({
                  host,
                  result,
                  fallbackQuery,
                  citation,
                  title,
                  subtitle: snippet,
                  preview: group.docTitle,
                  treeLast:
                    resultIndex === visibleHits.length - 1 &&
                    group.results.length <= visibleHits.length,
                  onNavigate: () => closeDocSourcePortal(trigger),
                });
              })}
            ${group.results.length > 20
              ? html`<span class="doc-source-overflow-note"
                  >+${hitsLabel(group.results.length - 20)}</span
                >`
              : nothing}
          </div>
        </div>
      </div> `,
    portalStyles: {
      zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
    },
    container: document.body,
    shadowDom: false,
    computePosition: {
      referenceElement: trigger,
      placement: 'bottom-end',
      middleware: streamLainDropdownMenuMiddleware(),
      autoUpdate: true,
    },
    abortController,
  });
  controller.portal = portal;
  decorateDocSourcePortal(controller, trigger);
  installDocSourcePortalDismissal(trigger, controller);
}

function toggleDocSourcePortal(
  event: MouseEvent,
  host: EventTarget,
  group: DocSourceGroup,
  fallbackQuery: string
) {
  event.preventDefault();
  event.stopPropagation();
  const trigger = event.currentTarget as HTMLElement;
  if (sourcePortalControllers.has(trigger)) {
    closeDocSourcePortal(trigger);
    return;
  }
  openDocSourcePortal(trigger, host, group, fallbackQuery);
}

function renderSourceListContent(
  host: EventTarget,
  groups: DocSourceGroup[],
  fallbackQuery: string
) {
  return html`<div class="doc-source-list streamlain-nested-display-list">
    ${groups.map((group, index) =>
      renderSourceGroupRow(host, group, fallbackQuery, index)
    )}
  </div>`;
}

export function toggleDocSearchCitationPortal(
  trigger: HTMLElement,
  event: MouseEvent,
  host: EventTarget,
  result: NormalizedAiDocSourcePillItem,
  fallbackQuery: string
) {
  event.preventDefault();
  event.stopPropagation();
  if (sourcePortalControllers.has(trigger)) {
    closeDocSourcePortal(trigger);
    return;
  }

  const abortController = new AbortController();
  abortController.signal.addEventListener('abort', () => {
    sourcePortalControllers.delete(trigger);
    trigger.setAttribute('aria-expanded', 'false');
  });
  const controller: DocSourcePortalController = {
    abortController,
    closing: false,
    directionTrackingInstalled: false,
    panelSelector: '.doc-source-citation-portal',
    portal: null,
  };
  sourcePortalControllers.set(trigger, controller);
  trigger.setAttribute('aria-expanded', 'true');

  const label = getDocSearchSourceLabel(
    result,
    result.title,
    undefined,
    citationLabelForResult(result)
  );
  const snippet =
    readableSourceSnippet(result.snippet) ||
    readableSourceSnippet(result.title) ||
    label;

  const { portal } = createLitPortal({
    template: html`${renderDocSourcePortalStyles()}
      <div
        class="doc-source-citation-portal streamlain-disclosure-popover-shell"
      >
        <span class="doc-source-citation-title">${label}</span>
        <div class="doc-source-citation-snippet">${snippet}</div>
        <div class="doc-source-citation-actions">
          <button
            class="doc-source-citation-open"
            @click=${(clickEvent: MouseEvent) => {
              clickEvent.preventDefault();
              clickEvent.stopPropagation();
              navigateToSourceResult(host, result, fallbackQuery);
              closeDocSourcePortal(trigger);
            }}
          >
            ${I18n.t('com.streamlain.ai.doc-search.open-source')}
          </button>
        </div>
      </div>`,
    portalStyles: {
      zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
    },
    container: document.body,
    shadowDom: false,
    computePosition: {
      referenceElement: trigger,
      placement: 'bottom-end',
      middleware: streamLainDropdownMenuMiddleware(),
      autoUpdate: true,
    },
    abortController,
  });
  controller.portal = portal;
  decorateDocSourcePortal(controller, trigger);
  installDocSourcePortalDismissal(trigger, controller);
}

export function getDocSearchSourceLabel(
  result: AiDocSourceResult,
  title: string,
  index?: number,
  citationPrefix?: string
) {
  const prefix =
    citationPrefix !== undefined
      ? `${citationPrefix} `
      : index === undefined
        ? ''
        : `${index + 1}. `;
  const isPdf = result.blockId && Number.isInteger(result.pageIndex);
  if (isPdf) {
    const attachmentName =
      readableSourceTitle(result.attachmentName) ||
      readableSourceTitle(stripPageSuffix(title)) ||
      readableSourceTitle(result.docTitle) ||
      'PDF';
    return `${prefix}${attachmentName} · ${pageLabel(result.pageIndex)}`;
  }
  return `${prefix}${readableSourceTitle(title) || readableSourceTitle(result.docTitle) || untitledDocLabel()}`;
}

function getSourceLabel(
  result: AiSourcePillItem,
  index?: number,
  citationPrefix?: string
): string {
  if (isMailSourceResult(result)) {
    const prefix =
      citationPrefix !== undefined
        ? `${citationPrefix} `
        : index === undefined
          ? ''
          : `${index + 1}. `;
    return `${prefix}${readableSourceTitle(result.title) || 'Mail'}`;
  }
  if (isWebSourceResult(result)) {
    const prefix =
      citationPrefix !== undefined
        ? `${citationPrefix} `
        : index === undefined
          ? ''
          : `${index + 1}. `;
    return `${prefix}${readableSourceTitle(result.title) || result.url}`;
  }
  return getDocSearchSourceLabel(result, result.title, index, citationPrefix);
}

export function getDocSearchSourceGroupCount(
  results: Array<AiDocSourcePillItem>
): number {
  return groupDocSearchSources(normalizeDocSearchSourceResults(results)).length;
}

export function navigateToDocSearchSource(
  target: EventTarget,
  result: AiDocSourceResult,
  fallbackQuery: string
) {
  const requestId = nanoid(8);
  const detail: AiDocSourceNavigationDetail = toAiDocSourceNavigationDetail(
    result,
    fallbackQuery,
    requestId
  );

  target.dispatchEvent(
    new CustomEvent('navigate-to-source', {
      bubbles: true,
      composed: true,
      detail,
    })
  );
  window.dispatchEvent(
    new CustomEvent('navigate-to-source', {
      detail,
    })
  );
}

export function navigateToMailSource(
  target: EventTarget,
  result: AiMailSourcePillItem
) {
  if (!result.messageId) return;
  const detail: AiMailSourceNavigationDetail = {
    kind: 'mail-source',
    messageId: result.messageId,
    subject: result.title,
    requestId: `mail:${result.messageId}`,
    ...(result.from ? { from: result.from } : {}),
    ...(result.receivedAt ? { receivedAt: result.receivedAt } : {}),
    ...(result.snippet ? { snippet: result.snippet } : {}),
  };

  target.dispatchEvent(
    new CustomEvent('navigate-to-source', {
      bubbles: true,
      composed: true,
      detail,
    })
  );
  window.dispatchEvent(
    new CustomEvent('navigate-to-source', {
      detail,
    })
  );
}

export function navigateToSourceResult(
  target: EventTarget,
  result: AiSourcePillItem,
  fallbackQuery: string
) {
  if (isMailSourceResult(result)) {
    navigateToMailSource(target, result);
    return;
  }
  if (isWebSourceResult(result)) {
    window.open(result.url, '_blank', 'noopener,noreferrer');
    return;
  }
  navigateToDocSearchSource(target, result, fallbackQuery);
}

export function renderSourcePills(
  host: EventTarget,
  results: Array<AiSourcePillItem>,
  fallbackQuery: string,
  max = 5
): TemplateResult<1> | typeof nothing {
  if (!results.length) {
    return nothing;
  }

  const groups = groupDocSearchSources(
    normalizeSourceResults(results).slice(0, MAX_RENDERED_SOURCE_RESULTS)
  );
  const visibleGroups = groups.slice(0, max);

  return html`<div class="doc-source-pills">
    ${visibleGroups.map((group, index) => {
      if (group.type === 'document') {
        const label = `${citationLabelForGroup(group, index)} ${group.title}`;
        return html`<span
          class="doc-source-group streamlain-disclosure-overlay-anchor streamlain-disclosure-overlay-down"
        >
          ${renderStreamLainDisclosurePill({
            ariaHasPopup: 'menu',
            className: 'doc-source-trigger doc-source-pill',
            expandable: true,
            label: html`<span class="doc-source-pill-title">${label}</span>`,
            meta: html`<span class="doc-source-pill-count"
              >${hitsLabel(group.results.length)}</span
            >`,
            title: groupTitle(group),
            onClick: (event: MouseEvent) =>
              toggleDocSourcePortal(event, host, group, fallbackQuery),
          })}
        </span>`;
      }

      const result = group.results[0];
      const label = getSourceLabel(
        result,
        undefined,
        citationLabelForResult(result, index)
      );
      const meta = isMailSourceResult(result) ? getMailSourceMeta(result) : '';
      const content = html`
        <span class="doc-source-pill-title">${label}</span>
        ${meta
          ? html`<span class="doc-source-pill-count">${meta}</span>`
          : nothing}
      `;
      if (!canNavigateSource(result)) {
        return renderStreamLainDisclosurePill({
          className: 'doc-source-pill doc-source-pill-static',
          label: content,
          title: getSourceTooltip(result, label),
        });
      }
      return renderStreamLainDisclosurePill({
        className: 'doc-source-pill',
        label: content,
        title: getSourceTooltip(result, label),
        onClick: (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          navigateToSourceResult(host, result, fallbackQuery);
        },
      });
    })}
    ${groups.length > max
      ? renderStreamLainDisclosurePill({
          className: 'doc-source-pill doc-source-pill-index',
          label: html`+${groups.length - max}`,
        })
      : nothing}
  </div>`;
}

export function renderDocSearchSourcePills(
  host: EventTarget,
  results: Array<AiDocSourcePillItem>,
  fallbackQuery: string,
  max = 5
): TemplateResult<1> | typeof nothing {
  return renderSourcePills(host, results, fallbackQuery, max);
}

export function renderSourceList(
  host: EventTarget,
  results: Array<AiSourcePillItem>,
  fallbackQuery: string
): TemplateResult<1> | typeof nothing {
  if (!results.length) {
    return nothing;
  }

  const normalizedResults = normalizeSourceResults(results).slice(
    0,
    MAX_RENDERED_SOURCE_RESULTS
  );
  const groups = groupDocSearchSources(normalizedResults);
  const summary = sourceListSummary(groups, normalizedResults, fallbackQuery);
  return renderStreamLainDisclosure({
    className: 'doc-source-history-shell',
    summary: html`<span
      class="doc-source-history-trigger doc-source-pill-title"
      title=${summary}
      >${summary}</span
    >`,
    content: renderSourceListContent(host, groups, fallbackQuery),
  });
}

export function renderDocSearchSourceList(
  host: EventTarget,
  results: Array<AiDocSourcePillItem>,
  fallbackQuery: string
): TemplateResult<1> | typeof nothing {
  return renderSourceList(host, results, fallbackQuery);
}

function groupDocSearchSources(
  results: Array<NormalizedAiSourcePillItem>
): DocSourceGroup[] {
  const groups: DocSourceGroup[] = [];
  const documentGroups = new Map<string, DocSourceGroup>();

  for (const result of results) {
    if (!isDocSourceResult(result)) {
      groups.push({
        type: 'single',
        key: getSourceGroupKey(result),
        title: getSourceGroupTitle(result),
        docTitle: isMailSourceResult(result) ? undefined : result.url,
        results: [result],
      });
      continue;
    }

    const key = `doc:${result.docId}`;
    let group = documentGroups.get(key);
    if (!group) {
      const title =
        readableSourceTitle(result.docTitle) ||
        readableSourceTitle(result.title) ||
        readableSourceTitle(result.attachmentName) ||
        untitledDocLabel();
      group = {
        type: 'document',
        key,
        title,
        docTitle: readableSourceTitle(result.docTitle) || undefined,
        results: [],
      };
      documentGroups.set(key, group);
      groups.push(group);
    }
    group.results.push(result);
  }

  for (const group of groups) {
    if (group.type === 'document' && group.results.length === 1) {
      group.type = 'single';
    } else if (
      group.type === 'document' &&
      group.results.every(
        result =>
          isDocSourceResult(result) &&
          result.blockId &&
          Number.isInteger(result.pageIndex)
      )
    ) {
      group.results.sort(
        (left, right) => (left.pageIndex ?? 0) - (right.pageIndex ?? 0)
      );
    }
  }

  return groups;
}

function renderSourceGroupRow(
  host: EventTarget,
  group: DocSourceGroup,
  fallbackQuery: string,
  index: number
): TemplateResult<1> {
  if (group.type === 'document') {
    return html`<details
      class="streamlain-nested-display-group doc-source-tree-group"
    >
      ${renderStreamLainNestedDisplayItemRow({
        action: html`<span class="doc-source-hit-count"
          >${hitsLabel(group.results.length)}</span
        >`,
        element: 'summary',
        expanded: false,
        icon: renderStreamLainDisplayItemLeadingLabel(`${index + 1}`),
        title: group.title,
        preview: group.docTitle,
        titleAttr: groupTitle(group),
      })}
      ${group.results.map((result, resultIndex) => {
        const title = getSourceHitTitle(result, group);
        const snippet = getSourceHitSnippet(result);
        return renderSourceHitRow({
          host,
          result,
          fallbackQuery,
          citation: '',
          title,
          subtitle: snippet,
          preview: group.docTitle,
          depth: 1,
          treeLast: resultIndex === group.results.length - 1,
        });
      })}
    </details>`;
  }

  const result = group.results[0];
  const label = getSourceLabel(result);
  const subtitle = isMailSourceResult(result)
    ? getMailSourceMeta(result)
    : isWebSourceResult(result)
      ? result.url
      : readableSourceTitle(result.docTitle) || undefined;
  const preview =
    readableSourceSnippet(result.snippet) ||
    readableSourceSnippet(result.title) ||
    undefined;
  const titleAttr = getSourceTooltip(result, label);
  const onClick = canNavigateSource(result)
    ? (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        navigateToSourceResult(host, result, fallbackQuery);
      }
    : undefined;

  return renderStreamLainNestedDisplayItemRow({
    icon: renderStreamLainDisplayItemLeadingLabel(`${index + 1}`),
    title: label,
    subtitle,
    preview,
    titleAttr,
    onClick,
    reserveToggleSpace: true,
  });
}

function renderSourceHitRow({
  host,
  result,
  fallbackQuery,
  citation,
  title,
  subtitle,
  preview,
  depth = 0,
  treeLast = false,
  onNavigate,
}: {
  citation: string;
  depth?: number;
  fallbackQuery: string;
  host: EventTarget;
  onNavigate?: () => void;
  preview?: string;
  result: NormalizedAiSourcePillItem;
  subtitle?: string;
  title: string;
  treeLast?: boolean;
}): TemplateResult<1> {
  return renderStreamLainNestedDisplayItemRow({
    depth,
    icon: renderStreamLainDisplayItemLeadingLabel(citation),
    title,
    subtitle,
    preview,
    titleAttr: subtitle || title,
    treeLast,
    onClick: canNavigateSource(result)
      ? (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          navigateToSourceResult(host, result, fallbackQuery);
          onNavigate?.();
        }
      : undefined,
  });
}

export function normalizeSourceResults<T extends AiSourcePillItem>(
  results: Array<T>
): Array<T & { citationId: string }> {
  return results.map((result, index) => ({
    ...result,
    citationId: readableCitationId(result.citationId) || `${index + 1}`,
  }));
}

export function normalizeDocSearchSourceResults(
  results: Array<AiDocSourcePillItem>
): NormalizedAiDocSourcePillItem[] {
  return normalizeSourceResults(results);
}

function groupTitle(group: DocSourceGroup): string {
  return group.docTitle && group.docTitle !== group.title
    ? `${group.docTitle} · ${group.title}`
    : group.title;
}

function citationLabelForResult(
  result: Pick<AiSourcePillItem, 'citationId'>,
  fallbackIndex?: number
): string {
  const id = readableCitationId(result.citationId);
  if (id) return `[${id}]`;
  if (fallbackIndex !== undefined) return `[${fallbackIndex + 1}]`;
  return '';
}

function citationLabelForGroup(
  group: DocSourceGroup,
  fallbackIndex: number
): string {
  const ids = compactCitationIds([
    ...new Set(
      group.results
        .map(result => readableCitationId(result.citationId))
        .filter((id): id is string => !!id)
    ),
  ]);
  if (!ids.length) return `[${fallbackIndex + 1}]`;
  return `[${ids.join(', ')}]`;
}

function compactCitationIds(ids: string[]): string[] {
  const numbers = ids.map(id => Number(id));
  if (numbers.some(number => !Number.isInteger(number))) {
    return ids;
  }

  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let previous = sorted[0];

  for (const number of sorted.slice(1)) {
    if (number === previous + 1) {
      previous = number;
      continue;
    }
    ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
    start = number;
    previous = number;
  }

  if (start !== undefined) {
    ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
  }

  return ranges;
}

function readableCitationId(value: string | undefined): string {
  const normalized = value?.trim() ?? '';
  return /^\d+$/.test(normalized) ? normalized : '';
}

function stripPageSuffix(title: string): string {
  return title.replace(/\s*[·-]\s*(?:S\.|Seite|Page|PDF page)\s*\d+$/i, '');
}

function readableSourceTitle(value: string | undefined): string {
  const normalized = value?.replace(/\s+/g, ' ').trim() ?? '';
  if (!normalized) return '';
  if (isOpaqueIdentifier(normalized)) return '';
  if (/^(?:S\.|Seite|p\.|Page)\s*\d+\b/i.test(normalized)) return '';
  return normalized;
}

function readableSourceSnippet(value: string | undefined): string {
  const normalized = sanitizeSourceText(value);
  if (!normalized) return '';
  const withoutPagePrefix = normalized
    .replace(/^(?:S\.|Seite|p\.|Page)\s*\d+\s*/i, '')
    .trim();
  if (!withoutPagePrefix || isOpaqueIdentifier(withoutPagePrefix)) return '';
  return withoutPagePrefix;
}

function sanitizeSourceText(value: string | undefined): string {
  return (
    value
      ?.replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim() ?? ''
  );
}

function isOpaqueIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(trimmed)) {
    return true;
  }
  const longIdentifierTokens = trimmed.match(/[A-Z0-9_-]{10,}/gi) ?? [];
  if (longIdentifierTokens.length === 0) return false;
  if (
    longIdentifierTokens.some(token => token.length >= 16 && /\d/u.test(token))
  ) {
    return true;
  }

  const words = trimmed.match(/\p{L}{3,}/gu) ?? [];
  return words.length <= 1;
}
